import WarningIcon from '@mui/icons-material/Warning';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { forwardRef, memo, useCallback } from 'react';
import { bloxValues } from 'themes/constants';
import { useTranslation } from 'react-i18next';
import { DragModal, DragModalProps, PossibleCloseEvent } from '../DragModal/DragModal';

// Simple prompt with yes or no actions
interface SimpleConfirmDeleteModalProps extends DragModalProps {
  onConfirm?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onCancel?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  headerTxt?: string;
  titleTxt?: string;
  subTxt?: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  confirmBtnDisabled?: boolean;
  cancelBtnDisabled?: boolean;
}

export const SimpleConfirmDeleteModal = memo(
  forwardRef<HTMLDivElement | null, SimpleConfirmDeleteModalProps>(
    (
      {
        onConfirm,
        onCancel,
        headerTxt,
        titleTxt,
        subTxt,
        cancelButtonText,
        confirmButtonText,
        confirmBtnDisabled = false,
        cancelBtnDisabled = false,
        ...props
      }: SimpleConfirmDeleteModalProps,
      ref,
    ) => {
      const theme = useTheme();
      const { t } = useTranslation();

      const resolvedHeaderText =
        headerTxt ?? t('blox.modal.simple_confirm_delete.header');
      const resolvedTitleText = titleTxt ?? t('blox.modal.simple_confirm_delete.title');
      const resolvedSubText = subTxt ?? t('blox.modal.simple_confirm_delete.description');
      const resolvedCancelButtonText =
        cancelButtonText ?? t('blox.modal.simple_confirm_delete.cancel');
      const resolvedConfirmButtonText =
        confirmButtonText ?? t('blox.modal.simple_confirm_delete.confirm');

      const handleConfirm = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
          if (onConfirm) {
            onConfirm(e);
          }
        },
        [onConfirm],
      );

      const handleCancel = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
          if (onCancel) {
            onCancel(e);
          }
        },
        [onCancel],
      );

      // no reason to have diff close/cancel
      // so just fwding to cancel handler for now
      const handleClose = useCallback(
        (event: PossibleCloseEvent) => {
          handleCancel(event as React.MouseEvent<HTMLButtonElement>);
        },
        [handleCancel],
      );

      return (
        <DragModal
          ref={ref}
          onClose={handleClose}
          initialWidth={400}
          initialHeight={300}
          preventBackdropClickClose={false}
          preventEscapeKeyClose={false}
          shouldConfirmClose={false}
          isDraggable={false}
          isResizable={false}
          isFullscreenable={false}
          header={
            <Box
              data-testid="simple-confirm-delete-header"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <WarningIcon
                color="error"
                sx={{ fontSize: bloxValues.iconSizeRegular }}
              />
              <Typography
                data-testid="simple-confirm-delete-header-text"
                sx={{
                  fontSize: bloxValues.xlFontSizeRem,
                  fontWeight: bloxValues.fontWeightMedium,
                  color: theme.palette.error.main,
                }}
              >
                {resolvedHeaderText}
              </Typography>
            </Box>
          }
          body={
            <Box
              data-testid="simple-confirm-delete-body-wrapper"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                // overflow fix for long text in small modal
                overflow: 'auto',
                height: '100%',
              }}
            >
              <Box
                data-testid="simple-confirm-delete-body-content"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'auto',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: bloxValues.xlFontSizeRem,
                    fontWeight: bloxValues.fontWeightMedium,
                    color: theme.palette.text.primary,
                  }}
                >
                  {resolvedTitleText}
                </Typography>
                <Typography
                  sx={{
                    fontSize: bloxValues.regularFontSizeRem,
                    fontWeight: bloxValues.fontWeightRegular,
                    color: theme.palette.text.secondary,
                  }}
                >
                  {resolvedSubText}
                </Typography>
              </Box>
            </Box>
          }
          footer={
            <Box
              data-testid="simple-confirm-delete-footer"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                gap: 2,
              }}
            >
              <Button
                data-testid="simple-confirm-delete-button"
                disableElevation={true}
                variant="contained"
                color="error"
                disabled={confirmBtnDisabled}
                onClick={handleConfirm}
              >
                {resolvedConfirmButtonText}
              </Button>
              <Button
                data-testid="simple-cancel-delete-button"
                disableElevation={true}
                variant="contained"
                color="primary"
                disabled={cancelBtnDisabled}
                onClick={handleCancel}
              >
                {resolvedCancelButtonText}
              </Button>
            </Box>
          }
          {...props}
        />
      );
    },
  ),
);
SimpleConfirmDeleteModal.displayName = 'SimpleConfirmDeleteModal';
